import {
    Button,
    MenuItem,
    Modal,
    ModalActions,
    modalClasses,
    ModalContent,
    ModalTitle,
} from "@kaltura/ds-react-components";
import { postKmsData, translate } from "@kaltura/mediaspace-shared-utils";
import { useRef, useState } from "react";
import { useToastsContext } from "@kaltura/mediaspace-shared-contexts/ToastsContext";
import { ButtonClickAnalyticsType, SharedMenuItemProps } from "@kaltura/mediaspace-shared-types";
import { useButtonAnalytics } from "@kaltura/mediaspace-shared-hooks";
import styled from "@emotion/styled";

export interface DeleteActionProps extends SharedMenuItemProps {
    entryId: string;
    entryName: string;
    apiUrl: string;
}

const StyledModal = styled(Modal)({
    [`& .${modalClasses.paper}`]: {
        maxWidth: 584,
    },
});

/**
 * Menu action for permanently deleting an entry from the recycle bin
 * @data-endpoint recyclebin-recycle-bin-ds-delete
 */
export const DeleteAction = ({ entryId, onCloseMenu, reloadMedia, autoFocus, apiUrl }: DeleteActionProps) => {
    const [openConfirmation, setOpenConfirmation] = useState(false);
    const [processing, setProcessing] = useState(false);

    const pendingReload = useRef(false);

    const { showToast } = useToastsContext();
    const sendButtonAnalytics = useButtonAnalytics();

    const closeConfirmation = () => {
        setOpenConfirmation(false);
        onCloseMenu?.();
    };

    const handleClick = () => {
        setOpenConfirmation(true);
        sendButtonAnalytics(
            "Delete item",
            ButtonClickAnalyticsType.DELETE,
            entryId,
            "recycle bin",
            "Menu - Recycle bin - Item actions"
        );
    };

    const handleDelete = async () => {
        setProcessing(true);
        try {
            const response = await postKmsData(apiUrl, { ids: [entryId] });
            if (response.success) {
                showToast({
                    severity: "success",
                    message: translate("media was permanently deleted"),
                });

                pendingReload.current = true;
                closeConfirmation();
            }
            else {
                showToast({ severity: "error", message: translate("Failed to permanently delete media") });
            }
        }
        catch (e: unknown) {
            console.error("Failed to permanently delete entry.", e);
            closeConfirmation();
            showToast({ severity: "error", message: translate("Failed to permanently delete media") });
        }
        finally {
            sendButtonAnalytics(
                "Confirm delete item",
                ButtonClickAnalyticsType.DELETE,
                entryId,
                "recycle bin",
                "Modal - Recycle bin - Permanently delete"
            );
            setProcessing(false);
        }
    };

    return (
        <>
            <MenuItem onClick={handleClick} autoFocus={autoFocus}>
                {translate("Delete")}
            </MenuItem>

            <StyledModal
                open={openConfirmation}
                onClose={closeConfirmation}
                maxWidth={false}
                slotProps={{
                    transition: {
                        onExited: () => {
                            if (pendingReload.current) {
                                // reloadMedia causes the component to unmount while the modal's exit transition is still running
                                // so the transition never completes and the modal appears to stay open (or closes abruptly/incorrectly).
                                // Deferring reloadMedia to onExited ensures the unmount only happens after the transition is fully done.
                                pendingReload.current = false;
                                reloadMedia?.();
                            }
                        },
                    },
                }}
            >
                <ModalTitle>{translate("Permanently delete")}</ModalTitle>
                <ModalContent>
                    {translate(
                        "Are you sure you want to permanently delete this media? Please note that this action cannot be undone."
                    )}
                </ModalContent>
                <ModalActions>
                    <Button variant={"borderless"} color={"secondary"} onClick={closeConfirmation}>
                        {translate("Cancel")}
                    </Button>
                    <Button color="danger" onClick={handleDelete} loading={processing} disabled={processing}>
                        {translate("Delete")}
                    </Button>
                </ModalActions>
            </StyledModal>
        </>
    );
};

export default DeleteAction;
